/* eslint-disable react-hooks/refs */
import { mapStyleAtom } from '@/atoms';
import { MapBranding } from '@/components/controls';
import { getAddressTypeColor, parseKeyValues, transformRequest } from '@/utils';
import { styles } from '@/utils/config';
import {
  Layers as LayersIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  ZoomOutMap as ZoomOutMapIcon,
} from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListSubheader,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  Typography,
} from '@mui/material';
import bbox from '@turf/bbox';
import { centerOfMass } from '@turf/turf';
import { useAtom } from 'jotai';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useCallback, useRef, useState } from 'react';
import Map, { Layer, ScaleControl, Source } from 'react-map-gl/maplibre';
import { MapButton } from '../MapButton';
import { AddressPopup } from './AddressPopup';
import './LocationMapDialog.css';

export function LocationMapDialog({ title, addresses, isOpen, onClose }) {
  const mapRef = useRef();
  const [style, setStyle] = useAtom(mapStyleAtom);
  const [styleMenuAnchorEl, setStyleMenuAnchorEl] = useState(null);
  const [mapHoverInfo, setMapHoverInfo] = useState(null);
  const [listHoverInfo, setListHoverInfo] = useState(null);
  const featureCollection = {
    type: 'FeatureCollection',
    features: (addresses ?? []).map(
      ({ point: geometry, ...properties }, id) => ({
        type: 'Feature',
        id,
        geometry,
        properties,
      }),
    ),
  };
  const boundingBox =
    (addresses ?? []).length > 0 ? bbox(featureCollection) : null;

  function handleStyleClick(event) {
    setStyleMenuAnchorEl(event.currentTarget);
  }

  function handleStyleMenuClose() {
    setStyleMenuAnchorEl(null);
  }

  const handleStyleMenuClick = (path) => () => {
    setStyle(path);
    setStyleMenuAnchorEl(null);
  };

  function handleZoomInClick() {
    mapRef.current.zoomIn();
  }

  function handleZoomOutClick() {
    mapRef.current.zoomOut();
  }

  function handleFitClick() {
    mapRef.current.fitBounds(bbox(featureCollection), {
      maxZoom: 16,
      padding: 32,
      duration: 1000,
    });
  }

  function handleLoad() {
    mapRef.current
      .getContainer()
      .querySelector('.maplibregl-ctrl-attrib')
      ?.classList.remove('maplibregl-compact-show');

    if (boundingBox) {
      mapRef.current.fitBounds(boundingBox, {
        maxZoom: 16,
        padding: 32,
        duration: 0,
      });
    }
  }

  const handleMouseMove = useCallback((event) => {
    const hoveredFeature =
      event.features && event.features[0]
        ? {
            ...event.features[0],
            properties: parseKeyValues(event.features[0].properties),
          }
        : null;

    setMapHoverInfo({
      feature: hoveredFeature,
      longitude: event.lngLat.lng,
      latitude: event.lngLat.lat,
    });
  }, []);

  const handleMouseOver = (feature) => () => {
    if (feature?.geometry) {
      const centre = centerOfMass(feature);

      setListHoverInfo({
        longitude: centre.geometry.coordinates[0],
        latitude: centre.geometry.coordinates[1],
        feature,
      });
    } else {
      setListHoverInfo(null);
    }
  };

  const handleClick = (feature) => () => {
    if (feature) {
      mapRef.current.fitBounds(bbox(feature), {
        padding: 40,
        duration: 1000,
        maxZoom: 16,
      });
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth={false}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box
          sx={(theme) => ({
            paddingBottom: 1,
            width: 640,
            height: 480,
            '& .maplibregl-popup-content': {
              backgroundColor: theme.palette.background.paper,
            },
            '& .maplibregl-popup-anchor-top .maplibregl-popup-tip': {
              borderBottomColor: theme.palette.background.paper,
            },
            '& .maplibregl-popup-anchor-bottom .maplibregl-popup-tip': {
              borderTopColor: theme.palette.background.paper,
            },
            '& .maplibregl-popup-anchor-left .maplibregl-popup-tip': {
              borderRightColor: theme.palette.background.paper,
            },
            '& .maplibregl-popup-anchor-right .maplibregl-popup-tip': {
              borderLeftColor: theme.palette.background.paper,
            },
          })}
        >
          <Map
            ref={mapRef}
            initialViewState={{
              bounds: boundingBox,
              fitBoundsOptions: { maxZoom: 16, padding: 32 },
            }}
            mapStyle={style}
            transformRequest={transformRequest(style)}
            style={{ borderRadius: 4 }}
            onLoad={handleLoad}
            interactiveLayerIds={['addresses-circle-layer']}
            onMouseMove={handleMouseMove}
          >
            {/* <MapImage sdf name="ir3-circle" url="/sdf/circle.png" />
            <MapImage sdf name="ir3-square" url="/sdf/square.png" />
            <MapImage sdf name="ir3-hexagon" url="/sdf/hexagon.png" />
            <MapImage sdf name="ir3-house" url="/sdf/house.png" />
            <MapImage sdf name="ir3-triangle" url="/sdf/triangle.png" /> */}
            <Stack spacing={1} sx={{ position: 'absolute', top: 8, left: 8 }}>
              <MapButton
                title="Zoom in"
                onClick={handleZoomInClick}
                disabled={
                  mapRef.current &&
                  mapRef.current.getZoom() === mapRef.current.getMaxZoom()
                }
              >
                <ZoomInIcon />
              </MapButton>
              <MapButton
                title="Zoom out"
                onClick={handleZoomOutClick}
                disabled={
                  mapRef.current &&
                  mapRef.current.getZoom() === mapRef.current.getMinZoom()
                }
              >
                <ZoomOutIcon />
              </MapButton>
              <MapButton
                title="Fit map"
                onClick={handleFitClick}
                disabled={!addresses?.[0]}
              >
                <ZoomOutMapIcon />
              </MapButton>
              <MapButton title="Change map style" onClick={handleStyleClick}>
                <LayersIcon />
              </MapButton>
              <Menu
                anchorEl={styleMenuAnchorEl}
                open={Boolean(styleMenuAnchorEl)}
                onClose={handleStyleMenuClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <MenuList
                  disablePadding
                  dense
                  subheader={<ListSubheader disableSticky>Type</ListSubheader>}
                >
                  {styles.map((layer) => (
                    <MenuItem
                      key={layer.path}
                      onClick={handleStyleMenuClick(layer.path)}
                      selected={layer.path === style}
                    >
                      {layer.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </Stack>
            <List
              disablePadding
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'background.paper',
                borderRadius: 2,
              }}
            >
              {featureCollection?.features.map((feature, index) => (
                <ListItemButton
                  key={index}
                  onClick={handleClick(feature)}
                  onMouseEnter={handleMouseOver(feature)}
                  onMouseLeave={handleMouseOver()}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center' }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        bgcolor: getAddressTypeColor(
                          feature.properties.typeCode,
                        ),
                        borderRadius: 6,
                      }}
                    />
                    <Typography variant="caption">
                      {feature.properties.label}
                    </Typography>
                  </Stack>
                </ListItemButton>
              ))}
            </List>
            <ScaleControl />
            <Source
              id="addresses"
              type="geojson"
              data={featureCollection}
              tolerance={0}
            >
              <Layer
                id="addresses-circle-layer"
                type="circle"
                paint={{
                  'circle-radius': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    5,
                    2,
                    12,
                    8,
                  ],
                  'circle-color': [
                    'match',
                    ['get', 'typeCode'],
                    'LSA',
                    getAddressTypeColor('LSA'),
                    'OCC',
                    getAddressTypeColor('OCC'),
                    getAddressTypeColor('DEFAULT'),
                  ],
                  'circle-opacity': 0.8,
                  'circle-stroke-width': 2,
                  'circle-stroke-color': '#fff',
                }}
              />
            </Source>
            <MapBranding style={style} />
            {mapHoverInfo?.feature && <AddressPopup hoverInfo={mapHoverInfo} />}
            {listHoverInfo && <AddressPopup hoverInfo={listHoverInfo} />}
          </Map>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
